import React, { useState, useEffect, useRef } from 'react';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import BuilderIllustration from '../illustrations/BuilderIllustration';

const BuildWithoutLimits = () => {
  const [ref, isVisible] = useScrollAnimation();
  const [step, setStep] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [xpPopups, setXpPopups] = useState([]);
  const [totalXP, setTotalXP] = useState(0);
  const [userName, setUserName] = useState('');
  const formContainerRef = useRef(null);

  const questions = [
    { id: 1, placeholder: "What's your name?", field: 'name' },
    { id: 2, placeholder: "What's your email?", field: 'email' },
    { id: 3, placeholder: "Tell us a bit about your project.", field: 'project' },
  ];

  const addXpPopup = (points, message, element) => {
    const id = Date.now() + Math.random();
    
    if (!formContainerRef.current || !element) return;
    
    const containerRect = formContainerRef.current.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    
    // Position relative to the form container
    const position = {
      x: elementRect.left - containerRect.left + (elementRect.width / 2),
      y: elementRect.top - containerRect.top - 20
    };
    
    const newPopup = { id, points, message, position, visible: true };
    setXpPopups(prev => [...prev, newPopup]);
    
    // Remove popup after animation
    setTimeout(() => {
      setXpPopups(prev => prev.filter(popup => popup.id !== id));
    }, 2000);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    // Award XP for typing (with cooldown to prevent spam)
    if (value.length > 0 && value.length % 5 === 0) {
      addXpPopup(1, "+1 XP Typing streak!", e.target);
      setTotalXP(prev => prev + 1);
    }

    // Special XP for name field
    if (step === 0 && value.length >= 2 && !userName) {
      addXpPopup(2, "+2 XP Aura elevated!", e.target);
      setTotalXP(prev => prev + 2);
      setUserName(value);
    }
  };

  const handleNext = () => {
    if (step < questions.length - 1) {
      // Award XP for completing a question
      const button = document.querySelector('button');
      addXpPopup(3, "+3 XP Question mastered!", button);
      setTotalXP(prev => prev + 3);

      setStep(step + 1);
      setInputValue('');
    } else {
      // Big XP for completion
      const button = document.querySelector('button');
      addXpPopup(10, "+10 XP Form completed! 🎉", button);
      setTotalXP(prev => prev + 10);
      setStep('done');
    }
  };

  // XP Popup Component - Now relative to form container
  const XpPopup = ({ points, message, position, visible }) => (
    <div
      className={`absolute z-50 transition-all duration-1000 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[-20px]'
      }`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: 'translateX(-50%)'
      }}
    >
      <div className="bg-gradient-to-r from-blue-600 to-green-400 text-black px-4 py-2 rounded-full text-sm font-bold shadow-lg border-2 border-yellow-400 animate-bounce">
        <span className="text-yellow-600 mr-1">✦</span>
        {message}
      </div>
    </div>
  );

  return (
    <section ref={ref} className="py-24 px-6 bg-white relative overflow-hidden" id='build'>
      {/* Illustration */}
      <div
        className={`absolute top-10 right-10 w-[320px] md:w-[420px] lg:w-[520px] opacity-25 transition-transform duration-[2000ms] ${
          isVisible ? 'translate-y-0' : 'translate-y-10'
        }`}
      >
        <BuilderIllustration className="w-full h-auto text-gray-400" />
      </div>

      {/* Content */}
      <div
        className={`max-w-6xl mx-auto transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="grid md:grid-cols-2 gap-16 items-center">
          {/* Left Text */}
          <div>
            <h2 className="text-5xl md:text-6xl font-bold mb-8 leading-tight text-black">
              Build faster.<br />Think less.
            </h2>
            <p className="text-lg md:text-xl text-gray-700 mb-10 leading-relaxed">
              Fylo's interface lets you design forms like you're sketching ideas.
              Drag - drop, and preview in real time, no setup screens, no clutter.
            </p>

            <div className="space-y-6">
              {['Gamified Forms', 'Fast Build', 'Custom themes', 'Share instantly'].map(
                (feature, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: '#00FFC6' }}
                    ></div>
                    <span className="text-lg font-medium text-black">{feature}</span>
                  </div>
                )
              )}
            </div>
          </div>

          {/* Right - Interactive Fylo-style form preview */}
          <div className="relative"> {/* Added relative container for positioning context */}
            <div ref={formContainerRef} className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-100 relative">
              {/* XP Popups - Now inside the form container */}
              {xpPopups.map(popup => (
                <XpPopup key={popup.id} {...popup} />
              ))}

              {/* XP Counter */}
              <div className="absolute -top-3 -right-3 bg-gradient-to-r from-blue-600 to-green-400 text-black px-4 py-1 rounded-full text-sm font-bold shadow-lg border-2 border-yellow-400">
                ✦ {totalXP} XP
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-200 flex flex-col justify-between h-[320px]">
                {step === 'done' ? (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <div className="text-4xl mb-4">🎉</div>
                    <p className="text-xl font-bold text-gray-800 mb-2">Amazing!</p>
                    <p className="text-gray-600 mb-4">You earned {totalXP} XP!</p>
                    <div className="bg-gradient-to-r from-blue-600 to-green-400 text-black px-4 py-2 rounded-full text-sm font-bold border-2 border-yellow-400">
                      ✦ Thank you for completing the form.
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col justify-center flex-grow transition-all duration-500 ease-out">
                    {/* Progress Bar */}
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
                      <div
                        className="bg-gradient-to-r from-blue-600 to-green-400 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${((step + 1) / questions.length) * 100}%` }}
                      ></div>
                    </div>

                    <p className="text-sm text-gray-500 mb-3">
                      Question {step + 1} of {questions.length}
                    </p>
                    
                    <input
                      type={step === 1 ? 'email' : 'text'}
                      value={inputValue}
                      placeholder={questions[step].placeholder}
                      onChange={handleInputChange}
                      className="w-full h-12 px-4 text-black rounded-lg border-2 border-gray-200 focus:border-blue-600 focus:outline-none transition-all duration-300 hover:border-gray-300"
                    />
                    
                    <button
                      onClick={handleNext}
                      disabled={!inputValue.trim()}
                      className={`mt-5 w-full h-11 rounded-lg font-medium transition-all duration-300 relative overflow-hidden ${
                        inputValue.trim()
                          ? 'bg-gradient-to-r from-blue-600 to-green-400 text-black hover:from-blue-700 hover:to-green-500 active:scale-[0.98] shadow-lg border-2 border-yellow-400'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      <span className="relative z-10">
                        {step === questions.length - 1 ? 'Finish Quest' : 'Next Challenge'}
                      </span>
                    </button>

                    {/* Mini XP Hints */}
                    <div className="mt-4 text-xs text-gray-500 text-center">
                      {step === 0 && "Pro tip: Type your name for bonus XP!"}
                      {step === 1 && "Every answered question brings you closer to victory!"}
                      {step === 2 && "Describe your project to unlock achievements!"}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BuildWithoutLimits;